// Registrar o desligamento de um vínculo (o modal "Gerar termo de rescisão" do Apps Script, agora
// para qualquer regime). No PJ, opcionalmente gera o termo de rescisão no Google Docs.
import { useEffect, useMemo, useState } from "react";
import { UserMinus } from "lucide-react";
import { getRhMotivosDesligamento, getRhStatusDocumentos, postRhDesligamento } from "../../api";
import { hojeISO } from "../estoque/comum";
import { AvisoErro, Campo, Modal, NumeroInput, btnStyle, inputStyle, parseNumero } from "../estoque/ui";

export default function DesligamentoModal({ C, contrato, nome, onClose, onConcluido }) {
  const ehPj = contrato.tipo_contratacao === "PJ";
  const [motivos, setMotivos] = useState([]);
  const [docsDisponiveis, setDocsDisponiveis] = useState(false);
  const [form, setForm] = useState({ data_final: hojeISO(), iniciativa: "", motivo_id: "", valor: "", equipamento: false,
    descricao_equipamento: "", observacao: "", gerar: false });
  const [erro, setErro] = useState("");
  const [salvando, setSalvando] = useState(false);
  const set = (dados) => setForm((f) => ({ ...f, ...dados }));

  useEffect(() => {
    getRhMotivosDesligamento().then((m) => setMotivos(m.filter((x) => x.ativo))).catch((e) => setErro(e.message));
    if (ehPj) {
      getRhStatusDocumentos().then((s) => { setDocsDisponiveis(s.disponivel); set({ gerar: s.disponivel }); }).catch(() => {});
    }
  }, [ehPj]);

  const porCategoria = useMemo(() => {
    const grupos = {};
    motivos.forEach((m) => { (grupos[m.categoria] = grupos[m.categoria] || []).push(m); });
    return Object.entries(grupos).sort(([a], [b]) => a.localeCompare(b, "pt-BR"));
  }, [motivos]);

  async function salvar() {
    setErro("");
    if (!form.data_final || !form.iniciativa || !form.motivo_id) return setErro("Preencha data final, iniciativa e motivo.");
    if (form.equipamento && !form.descricao_equipamento.trim()) return setErro("Descreva o equipamento a devolver.");
    setSalvando(true);
    try {
      const r = await postRhDesligamento(contrato.id, {
        data_final: form.data_final, iniciativa: form.iniciativa, motivo_id: Number(form.motivo_id),
        valor_rescisao: parseNumero(form.valor), equipamento_devolver: form.equipamento,
        descricao_equipamento: form.descricao_equipamento || null, observacao: form.observacao || null,
        gerar_documento: ehPj && form.gerar,
      });
      onConcluido(r);
    } catch (e) {
      setErro(e.message);
      setSalvando(false);
    }
  }

  return (
    <Modal C={C} titulo={`Desligar · ${nome}`} onClose={onClose} largura={640}
      rodape={<>
        <button onClick={onClose} style={btnStyle(C.muted, true)}>Cancelar</button>
        <button onClick={salvar} disabled={salvando} style={{ ...btnStyle(C.danger), opacity: salvando ? 0.6 : 1 }}>
          <UserMinus size={15} /> {salvando ? (form.gerar ? "Gerando termo…" : "Salvando…") : "Registrar desligamento"}
        </button>
      </>}>
      <AvisoErro C={C} onClose={() => setErro("")}>{erro}</AvisoErro>
      <p style={{ fontSize: 12, color: C.muted, margin: "0 0 12px" }}>
        Vínculo {contrato.tipo_contratacao}{contrato.empresa ? ` · ${contrato.empresa}` : ""}. Ao registrar, o vínculo fica inativo.
      </p>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 12 }}>
        <Campo C={C} label="Data final do contrato" obrigatorio>
          <input type="date" value={form.data_final} onChange={(e) => set({ data_final: e.target.value })} style={inputStyle(C)} />
        </Campo>
        <Campo C={C} label="Iniciativa" obrigatorio ajuda={form.iniciativa ? (form.iniciativa === "colaborador" ? "Voluntário" : "Involuntário") : null}>
          <select value={form.iniciativa} onChange={(e) => set({ iniciativa: e.target.value })} style={inputStyle(C)}>
            <option value="">Selecione</option>
            <option value="empresa">Da empresa</option>
            <option value="colaborador">Do colaborador / prestador</option>
          </select>
        </Campo>
        <Campo C={C} label={ehPj ? "Valor da rescisão (R$)" : "Valor (R$)"}>
          <NumeroInput C={C} casas={2} value={form.valor} onChange={(v) => set({ valor: v })} />
        </Campo>
      </div>
      <div style={{ marginTop: 12 }}>
        <Campo C={C} label="Motivo" obrigatorio>
          <select value={form.motivo_id} onChange={(e) => set({ motivo_id: e.target.value })} style={inputStyle(C)}>
            <option value="">Selecione</option>
            {porCategoria.map(([categoria, itens]) => (
              <optgroup key={categoria} label={categoria}>
                {itens.map((m) => <option key={m.id} value={m.id}>{m.motivo}</option>)}
              </optgroup>
            ))}
          </select>
        </Campo>
      </div>
      <label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 13, marginTop: 12 }}>
        <input type="checkbox" checked={form.equipamento} onChange={(e) => set({ equipamento: e.target.checked })} /> Há equipamento da empresa a devolver
      </label>
      {form.equipamento && (
        <div style={{ marginTop: 8 }}>
          <Campo C={C} label="Equipamento" obrigatorio>
            <input value={form.descricao_equipamento} onChange={(e) => set({ descricao_equipamento: e.target.value })} placeholder="ex.: Notebook Dell Latitude, nº de patrimônio…" style={inputStyle(C)} />
          </Campo>
        </div>
      )}
      <div style={{ marginTop: 12 }}>
        <Campo C={C} label="Observação">
          <textarea value={form.observacao} onChange={(e) => set({ observacao: e.target.value })} rows={3} style={{ ...inputStyle(C), resize: "vertical" }} />
        </Campo>
      </div>
      {ehPj && (
        <label style={{ display: "flex", gap: 8, alignItems: "flex-start", fontSize: 13, marginTop: 12, color: docsDisponiveis ? C.text : C.muted }}>
          <input type="checkbox" checked={form.gerar} disabled={!docsDisponiveis} onChange={(e) => set({ gerar: e.target.checked })} style={{ marginTop: 3 }} />
          <span>
            Gerar o termo de rescisão no Google Docs agora
            {!docsDisponiveis && <span style={{ display: "block", fontSize: 11 }}>Indisponível: o modelo do Drive ainda não está acessível. Registre agora e gere o termo depois.</span>}
          </span>
        </label>
      )}
    </Modal>
  );
}
